// TelcoTrack — režim importu budov/tabulek: upsert (výchozí) / jen update / jen create.

export const OBJECT_IMPORT_MODES = ['upsert', 'update', 'create'] as const;
export type ObjectImportMode = (typeof OBJECT_IMPORT_MODES)[number];

export const DEFAULT_OBJECT_IMPORT_MODE: ObjectImportMode = 'upsert';

export const OBJECT_IMPORT_MODE_SKIP_CREATE = 'mode_skip_create';
export const OBJECT_IMPORT_MODE_SKIP_UPDATE = 'mode_skip_update';

export const OBJECT_IMPORT_MODE_OPTIONS: ReadonlyArray<{
  value: ObjectImportMode;
  labelCs: string;
  hintCs: string;
}> = [
  {
    value: 'upsert',
    labelCs: 'Aktualizovat i vytvořit',
    hintCs: 'Spárované obohatí, nespárované založí.',
  },
  {
    value: 'update',
    labelCs: 'Pouze aktualizovat existující',
    hintCs: 'Jen existující budovy. Nové řádky přeskočí.',
  },
  {
    value: 'create',
    labelCs: 'Pouze vytvořit nové',
    hintCs: 'Jen nové budovy. Existující nepřepíše.',
  },
];

export function isObjectImportMode(value: unknown): value is ObjectImportMode {
  return value === 'upsert' || value === 'update' || value === 'create';
}

/** Neznámá hodnota → výchozí upsert (původní chování). */
export function parseObjectImportMode(value: unknown): ObjectImportMode {
  return isObjectImportMode(value) ? value : DEFAULT_OBJECT_IMPORT_MODE;
}

export function shouldEnrichOnMatch(mode: ObjectImportMode): boolean {
  return mode !== 'create';
}

export function shouldCreateOnUnmatched(mode: ObjectImportMode): boolean {
  return mode !== 'update';
}

export function isImportModeSkipReason(reason: string | undefined): boolean {
  return reason === OBJECT_IMPORT_MODE_SKIP_CREATE || reason === OBJECT_IMPORT_MODE_SKIP_UPDATE;
}

/**
 * Unified preview: matched → enrich, unmatched → create (upsert).
 * `update` přeskočí create; `create` přeskočí enrich.
 */
export function suggestUnifiedImportAction(
  matchStatus: 'matched' | 'unmatched' | 'ambiguous',
  mode: ObjectImportMode,
): 'enrich' | 'create' | 'skip' {
  if (matchStatus === 'ambiguous') return 'skip';
  if (matchStatus === 'matched') {
    return shouldEnrichOnMatch(mode) ? 'enrich' : 'skip';
  }
  return shouldCreateOnUnmatched(mode) ? 'create' : 'skip';
}

/** ROPID create/update plán po aplikaci režimu. */
export function applyImportModeToRowAction(
  action: 'create' | 'update' | 'skip',
  mode: ObjectImportMode,
): { action: 'create' | 'update' | 'skip'; skipReason?: string } {
  if (action === 'skip') return { action: 'skip' };
  if (action === 'create' && !shouldCreateOnUnmatched(mode)) {
    return { action: 'skip', skipReason: OBJECT_IMPORT_MODE_SKIP_CREATE };
  }
  if (action === 'update' && !shouldEnrichOnMatch(mode)) {
    return { action: 'skip', skipReason: OBJECT_IMPORT_MODE_SKIP_UPDATE };
  }
  return { action };
}
